import React, { useState, useEffect } from 'react';
import Styles from './BrandPreview.module.scss';
import {
  FiX,
  FiExternalLink,
  FiFileText,
  FiTwitter,
  FiTrendingUp,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiTarget,
  FiGlobe,
  FiUpload,
  FiLink,
  FiZap,
  FiArrowRight,
  FiUsers
} from 'react-icons/fi';
import onBoardingService from 'services/onBoardingService';
import LoadingState from 'Components/LoadingState/LoadingState';

const BrandPreview = ({ brand, onClose, onEdit, onShare }) => {
  const [contentSamples, setContentSamples] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (brand?.id) {
      fetchContentSamples();
    }
  }, [brand]);

  const fetchContentSamples = async () => {
    try {
      setLoading(true);
      const response = await onBoardingService.listContentSamples(brand.id);
      setContentSamples(response?.data || []);
    } catch (error) {
      console.error('Error fetching content samples:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate completion breakdown
  const getCompletionBreakdown = () => {
    const sections = [
      { name: 'Brand Identity', completed: !!brand.brand_name, icon: FiTarget },
      { name: 'Brand Details', completed: !!(brand.url || brand.description), icon: FiGlobe },
      { name: 'Industries', completed: brand.industries?.length > 0, icon: FiTrendingUp },
      { name: 'Target Audience', completed: brand.target_audience?.length > 0, icon: FiTarget },
      { name: 'Content Samples', completed: brand.content_samples_count > 0, icon: FiFileText },
      { name: 'Social Channels', completed: brand.social_channels_count > 0, icon: FiTwitter },
      { name: 'Content Topics', completed: brand.content_topics?.length > 0, icon: FiZap }
    ];
    return sections;
  };

  const completionBreakdown = getCompletionBreakdown();
  const completedSections = completionBreakdown.filter(s => s.completed).length;
  const totalSections = completionBreakdown.length;
  const completionPercentage = Math.round((completedSections / totalSections) * 100);

  // Get status color
  const getStatusColor = () => {
    if (completionPercentage >= 80) return 'teal';
    if (completionPercentage >= 50) return 'coral';
    return 'lavender';
  };

  const statusColor = getStatusColor();

  return (
    <div className={Styles.overlay} onClick={onClose}>
      <div className={Styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={Styles.header}>
          <div className={Styles.headerLeft}>
            {brand.logo ? (
              <img src={brand.logo} alt={brand.brand_name} className={Styles.logo} />
            ) : (
              <div className={Styles.logoPlaceholder}>
                {brand.brand_name?.charAt(0) || 'B'}
              </div>
            )}
            <div>
              <h2 className={Styles.brandName}>{brand.brand_name || 'Unnamed Brand'}</h2>
              <p className={Styles.brandIndustry}>
                {brand.industries?.join(', ') || 'No industry set'}
              </p>
            </div>
          </div>
          <div className={Styles.headerActions}>
            <button className={Styles.shareButton} onClick={() => onShare?.(brand)} title="Share Brand">
              <FiUsers size={20} />
            </button>
            <button className={Styles.closeButton} onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={Styles.content}>
          {/* Quick Stats */}
          <div className={Styles.quickStats}>
            <div className={`${Styles.statBadge} ${Styles.statBadgeTeal}`}>
              <FiFileText size={18} />
              <div>
                <div className={Styles.statBadgeValue}>{brand.content_samples_count || 0}</div>
                <div className={Styles.statBadgeLabel}>Content Samples</div>
              </div>
            </div>
            <div className={`${Styles.statBadge} ${Styles.statBadgeCoral}`}>
              <FiTwitter size={18} />
              <div>
                <div className={Styles.statBadgeValue}>{brand.social_channels_count || 0}</div>
                <div className={Styles.statBadgeLabel}>Social Channels</div>
              </div>
            </div>
            <div className={`${Styles.statBadge} ${Styles.statBadgeLavender}`}>
              <FiTrendingUp size={18} />
              <div>
                <div className={Styles.statBadgeValue}>{brand.content_topics?.length || 0}</div>
                <div className={Styles.statBadgeLabel}>Topics</div>
              </div>
            </div>
          </div>

          {/* Training Progress */}
          <div className={Styles.section}>
            <div className={Styles.sectionHeader}>
              <h3 className={Styles.sectionTitle}>
                <FiZap size={18} />
                Training Progress
              </h3>
              <span className={`${Styles.statusBadge} ${Styles[`statusBadge${statusColor.charAt(0).toUpperCase() + statusColor.slice(1)}`]}`}>
                {completionPercentage}% Complete
              </span>
            </div>

            {/* Progress Bar */}
            <div className={Styles.progressBar}>
              <div
                className={`${Styles.progressFill} ${Styles[`progressFill${statusColor.charAt(0).toUpperCase() + statusColor.slice(1)}`]}`}
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>

            {/* Completion Breakdown */}
            <div className={Styles.completionGrid}>
              {completionBreakdown.map((section, idx) => (
                <div
                  key={idx}
                  className={`${Styles.completionItem} ${section.completed ? Styles.completionItemComplete : ''}`}
                >
                  {section.completed ? (
                    <FiCheckCircle size={16} className={Styles.completionIconComplete} />
                  ) : (
                    <FiAlertCircle size={16} className={Styles.completionIconIncomplete} />
                  )}
                  <span className={Styles.completionItemText}>{section.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Brand Details */}
          {(brand.description || brand.url) && (
            <div className={Styles.section}>
              <h3 className={Styles.sectionTitle}>
                <FiGlobe size={18} />
                Brand Details
              </h3>
              {brand.description && (
                <p className={Styles.description}>{brand.description}</p>
              )}
              {brand.url && (
                <a
                  href={brand.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={Styles.websiteLink}
                >
                  <FiLink size={16} />
                  {brand.url}
                  <FiExternalLink size={14} />
                </a>
              )}
            </div>
          )}

          {/* Target Audience */}
          {brand.target_audience && brand.target_audience.length > 0 && (
            <div className={Styles.section}>
              <h3 className={Styles.sectionTitle}>
                <FiTarget size={18} />
                Target Audience
              </h3>
              <div className={Styles.tagsList}>
                {brand.target_audience.map((audience, idx) => (
                  <span key={idx} className={Styles.tag}>{audience}</span>
                ))}
              </div>
            </div>
          )}

          {/* Content Topics */}
          {brand.content_topics && brand.content_topics.length > 0 && (
            <div className={Styles.section}>
              <h3 className={Styles.sectionTitle}>
                <FiTrendingUp size={18} />
                Content Topics
              </h3>
              <div className={Styles.tagsList}>
                {brand.content_topics.slice(0, 10).map((topic, idx) => (
                  <span key={idx} className={Styles.tag}>{topic}</span>
                ))}
                {brand.content_topics.length > 10 && (
                  <span className={Styles.tagMore}>+{brand.content_topics.length - 10} more</span>
                )}
              </div>
            </div>
          )}

          {/* Recent Content Samples */}
          {loading ? (
            <div className={Styles.section}>
              <LoadingState text="Loading content samples..." />
            </div>
          ) : contentSamples.length > 0 ? (
            <div className={Styles.section}>
              <h3 className={Styles.sectionTitle}>
                <FiFileText size={18} />
                Recent Content Samples ({contentSamples.length})
              </h3>
              <div className={Styles.samplesList}>
                {contentSamples.slice(0, 3).map((sample, idx) => (
                  <div key={idx} className={Styles.sampleCard}>
                    <div className={Styles.sampleIcon}>
                      {sample.content_type === 'tweet' ? <FiTwitter size={16} /> : <FiFileText size={16} />}
                    </div>
                    <div className={Styles.sampleContent}>
                      <p className={Styles.sampleText}>
                        {sample.content?.substring(0, 150)}
                        {sample.content?.length > 150 ? '...' : ''}
                      </p>
                      {sample.created_at && (
                        <div className={Styles.sampleMeta}>
                          <FiClock size={12} />
                          {new Date(sample.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {contentSamples.length > 3 && (
                <button className={Styles.viewAllButton} onClick={() => onEdit(brand)}>
                  View all {contentSamples.length} samples
                  <FiArrowRight size={16} />
                </button>
              )}
            </div>
          ) : null}

          {/* Last Updated */}
          {brand.updated_at && (
            <div className={Styles.lastUpdated}>
              <FiClock size={14} />
              Last updated {new Date(brand.updated_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className={Styles.footer}>
          <button className={Styles.secondaryButton} onClick={onClose}>
            Close
          </button>
          <button className={Styles.primaryButton} onClick={() => onEdit(brand)}>
            <FiUpload size={18} />
            Open Training Module
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandPreview;
